import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LanePolylineApi from '../lib/api/LanePolylineApi';

/**
 * One lane's full geometry.
 *
 * `staleTime: Infinity` and `refetchOnWindowFocus: false` on purpose: the
 * payload carries the `updatedAt` the commit sends back as its concurrency
 * token, and a background refetch mid-edit would swap that token underneath
 * an operator whose local edits were built on the older geometry — their
 * save would then either fail confusingly or, worse, succeed against
 * geometry they never saw. The editor loads once, and a rejected commit is
 * what prompts a deliberate reload.
 */
export const useLanePolyline = (laneId) => {
  const query = useQuery({
    queryKey: ['lane-polyline', laneId],
    queryFn: () => LanePolylineApi.fetchPolyline(laneId),
    enabled: !!laneId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return {
    lane: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    // True while a refetch is in flight over data already held — which is
    // exactly the window after a commit invalidates this query. During it,
    // `lane` is still the PRE-SAVE geometry and, more dangerously, the
    // pre-save `updatedAt`. A caller that lets an operator keep editing
    // through that window can build a candidate from stale geometry and then
    // send it with the refreshed token, which the backend accepts — undoing
    // part of the save outside the newly selected stretch.
    isRefreshing: query.isFetching && !query.isLoading,
  };
};

/**
 * Snap / preview / commit.
 *
 * Every mutate function resolves `{ success, data }` rather than throwing,
 * matching `useStationMutations` — call sites toast on `success: false`, and
 * a throw here would break that shape.
 */
export const useLanePolylineMutations = (laneId) => {
  const queryClient = useQueryClient();

  const snapMutation = useMutation({
    mutationFn: (body) => LanePolylineApi.snapSection(laneId, body),
  });

  const previewMutation = useMutation({
    mutationFn: (candidateGeoJson) =>
      LanePolylineApi.previewPolyline(laneId, candidateGeoJson),
  });

  const commitMutation = useMutation({
    mutationFn: (body) => LanePolylineApi.commitPolyline(laneId, body),
    onSuccess: (data) => {
      // Every lane the server ACTUALLY wrote, not just the one being edited.
      // A commit can write the reverse lane too (mirrored, or re-snapped), so
      // invalidating `laneId` alone left that lane's geometry, concurrency
      // token, stations and buffer cached from before the write. Reopening
      // its editor would then load stale geometry with a stale `updatedAt` —
      // and that token is what the save is checked against, so the operator's
      // next edit there would fail as a conflict they could not explain.
      //
      // Read from the response rather than recomputing which lanes were
      // affected: `updatedLaneIds` is the server's own record of what it
      // wrote, so this cannot drift from the reverse-lane rules in
      // `LanePolylineService.commit`.
      const writtenIds = data?.updatedLaneIds?.length
        ? data.updatedLaneIds
        : [laneId];

      writtenIds.forEach((id) => {
        // The geometry AND its concurrency token are now stale, as is the
        // history list.
        queryClient.invalidateQueries({ queryKey: ['lane-polyline', id] });
        queryClient.invalidateQueries({ queryKey: ['lane-polyline-history', id] });
        // Both derived from the SAME geometry. Without these the operator is
        // redirected to the lane map and sees the new line drawn over the old
        // corridor buffer with stale station verdicts — precisely the "verify
        // in the lane map that the corridor moved" step they were just sent to
        // perform. `lane-stations` holds for 5 minutes and `lane-buffer` has
        // `staleTime: Infinity`, so neither recovers on its own.
        queryClient.invalidateQueries({ queryKey: ['lane-stations', id] });
        // `lane-buffer` keys are `['lane-buffer', id, radiusMeters]` — one
        // entry per radius the operator has dragged through. Invalidating by
        // the `[key, id]` PREFIX catches all of them; react-query matches
        // query keys by prefix unless told otherwise.
        queryClient.invalidateQueries({ queryKey: ['lane-buffer', id] });
      });

      // Not per-lane: one query draws every lane on the map.
      queryClient.invalidateQueries({ queryKey: ['lane-map-lanes'] });
    },
  });

  const run = async (mutation, input) => {
    try {
      const data = await mutation.mutateAsync(input);
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  return {
    snapSection: (body) => run(snapMutation, body),
    previewPolyline: (candidateGeoJson) => run(previewMutation, candidateGeoJson),
    commitPolyline: (body) => run(commitMutation, body),
    isSnapping: snapMutation.isLoading,
    isPreviewing: previewMutation.isLoading,
    isCommitting: commitMutation.isLoading,
  };
};
